import React from 'react';
import clsx from 'clsx';
import {motion} from 'motion/react';
import camelCase from 'lodash/camelCase';

import {assert} from 'common/util';
import {Action, MetricEntry} from 'common/query-builder/query-entry-section/types';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {useTranslation} from 'common/i18n/hooks';
import {MetricUnitsButton} from 'common/query-builder/chart-customization/metric-units';
import {CallingLocation} from 'common/query-builder/chart-customization/tracking';

import styles from './index.module.styl';
import {MetricParts} from './types/catalog';
import {MetricBehaviorBase} from './types/base';
import {OverflowControls} from './common/controls';

export function NewMetricEntryRow(props: EntryRowProps<MetricEntry>) {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const {t} = useTranslation();

  const metric = props.entry;
  const behavior = metric.behavior;

  /** @todo implement all these methods */
  const handleSaveMetric = () => true;
  const handleHideMetric = () => true;
  const handleDuplicateMetric = () => true;
  const handleRemoveMetric = () => true;

  const handleCollapse = () => {
    setIsExpanded(!isExpanded);
  };

  assert(behavior.type in MetricParts, `attempted to render an unsupported metric type`);

  const MetricMeasurement = MetricParts[behavior.type]!.measurement;
  const MetricBehavior = MetricParts[behavior.type]!.definition;

  return (
    <div className={clsx(styles.entry, styles[camelCase(`is-${behavior.type}-metric`)])}>
      {/* metric header area */}
      <div className={styles.header}>
        {/* icon/collapse button */}
        <ButtonReact className={styles.collapseButton} theme={ButtonTheme.Tertiary} onClick={handleCollapse}>
          <span className={styles.label}>{props.rowLabel}</span>
          <SvgIconReact className={styles.caret} icon='caret-down' />
        </ButtonReact>

        {/* metric label/selector */}
        <div className={styles.title}>{props.entry.name || `New Metric Definition`}</div>

        {/* metric controls */}
        <div className={styles.actions}>
          <ButtonReact theme={ButtonTheme.Tertiary} icon='x' />

          {/* metric overflow controls for saving, resetting, etc... */}
          <OverflowControls
            items={[
              {
                items: [
                  {
                    label: t(`query-builder.actions.save-metric`),
                    icon: `save`,
                    onSelect: handleSaveMetric,
                  },
                  {
                    label: t(`query-builder.actions.hide-metric`),
                    icon: `eye-on`,
                    onSelect: handleHideMetric,
                  },
                  {
                    label: t(`query-builder.actions.duplicate`),
                    icon: `duplicate`,
                    onSelect: handleDuplicateMetric,
                  },
                  {
                    label: t(`query-builder.actions.remove`),
                    icon: `trashcan`,
                    onSelect: handleRemoveMetric,
                  },
                ],
              },
            ]}
          />
        </div>
      </div>

      {/* metric body */}
      <motion.div
        className={styles.body}
        transition={{
          type: `spring`,
          visualDuration: 0.1,
          bounce: 0.3,
        }}
        initial={isExpanded}
        animate={isExpanded ? `expanded` : `collapsed`}
        variants={{
          expanded: {height: `auto`},
          collapsed: {height: 0},
        }}
      >
        <div className={styles.content}>
          <MetricBehaviorBase behavior={props.entry.behavior}>
            <MetricBehavior {...props} />
          </MetricBehaviorBase>
        </div>

        {/* metric measurement options */}
        <div className={styles.measurement}>
          <MetricUnitsButton
            callingLocation={CallingLocation.Metric}
            chartType={props.currentChartType}
            entry={props.entry}
            setEntry={(entry) => props.updateEntry(Action.Edit, entry as MetricEntry, {requery: false})}
            showDefaultMetricUnitSelector={props.showDefaultMetricUnitSelector}
          />
          <MetricMeasurement {...props} />
        </div>
      </motion.div>
    </div>
  );
}
